// Plays a recorded kissaderby.fi race with its own timing: the same frames the live socket sent.
// Arrival times are on the recording's own clock (ms since the recording began, plus a loop offset),
// so the race keeps its real rhythm at any playback speed. It prebuffers 2 s so the broadcast delay is
// full from the first frame. After the end it loops over the complete race cycles only
// (from the first post time to the last), so a loop never splices two different races together.
import type { RaceSource, SignalFrame } from '../../race/types';
import { decode } from '../decode';
import { parseTsv } from './tsv';
import { REPLAY_LABEL, realScheduler, type Scheduler, type SourceStatus } from './types';

export const PREBUFFER_MS = 2000;
const TICK_MS = 50;

interface Entry { ms: number; frame: SignalFrame }

export interface ReplayOptions {
  seekMs?: number;
  speed?: number; // 0.25 .. 8
  prebufferMs?: number;
  loop?: boolean;
}

export class ReplaySource implements RaceSource {
  readonly kind = 'replay' as const;
  readonly label = REPLAY_LABEL;
  readonly speed: number;
  private readonly entries: Entry[];
  private readonly loopStart: number; // index of the first starting frame
  private readonly loopEnd: number; // index of the last starting frame (exclusive end of the loop)
  private readonly seekMs: number;
  private readonly prebufferMs: number;
  private readonly loop: boolean;
  private index = 0;
  private offset = 0; // added to recording ms after each loop
  private realStart = 0;
  private timer: unknown = null;
  private onFrame: (f: SignalFrame, arrivalMs: number) => void = () => {};

  constructor(tsvText: string, opts: ReplayOptions = {}, private readonly sched: Scheduler = realScheduler) {
    this.entries = [];
    for (const line of parseTsv(tsvText)) {
      const frame = decode(line.b64);
      if (frame) this.entries.push({ ms: line.ms, frame });
    }
    if (!this.entries.length) throw new Error('The recording holds no frames');
    this.speed = Math.min(8, Math.max(0.25, opts.speed ?? 1));
    this.prebufferMs = opts.prebufferMs ?? PREBUFFER_MS;
    this.loop = opts.loop ?? true;
    const starts = this.entries.map((e, i) => (e.frame.state === 'starting' && this.entries[i - 1]?.frame.state !== 'starting' ? i : -1)).filter((i) => i >= 0);
    this.loopStart = starts[0] ?? 0;
    this.loopEnd = starts.length > 1 ? starts[starts.length - 1]! : this.entries.length;
    // Seek is on the recording's own clock (ms since the recording began), wrapping past the end.
    const end = this.entries[this.entries.length - 1]!.ms;
    this.seekMs = Math.max(0, opts.seekMs ?? 0) % Math.max(1, end);
  }

  /** Recording length in ms. */
  get durationMs(): number {
    return this.entries[this.entries.length - 1]!.ms - this.entries[0]!.ms;
  }

  start(onFrame: (f: SignalFrame, arrivalMs: number) => void, onStatus: (s: SourceStatus) => void): void {
    this.stop();
    this.onFrame = onFrame;
    this.index = Math.max(0, this.entries.findIndex((e) => e.ms >= this.seekMs));
    this.offset = 0;
    this.realStart = this.sched.now();
    onStatus('open');
    this.pump();
  }

  stop(): void {
    this.sched.clearTimeout(this.timer);
    this.timer = null;
  }

  /** Recording time (with loop offset) that has been reached now, including the prebuffer. */
  private horizon(): number {
    return this.seekMs + (this.sched.now() - this.realStart) * this.speed + this.prebufferMs;
  }

  private pump = (): void => {
    const until = this.horizon();
    let guard = this.entries.length * 2;
    while (guard-- > 0) {
      if (this.index >= this.loopEnd || this.index >= this.entries.length) {
        if (!this.loop || this.loopEnd <= this.loopStart) { this.timer = null; return; }
        const end = this.entries[this.loopEnd]?.ms ?? this.entries[this.entries.length - 1]!.ms + 1000;
        this.offset += end - this.entries[this.loopStart]!.ms;
        this.index = this.loopStart;
      }
      const e = this.entries[this.index]!;
      const at = e.ms + this.offset;
      if (at > until) break;
      this.index++;
      this.onFrame(e.frame, at);
    }
    this.timer = this.sched.setTimeout(this.pump, TICK_MS);
  };
}
